import {
	AsyncSelect,
	AsyncSelectProps,
	Badge,
	Dropdown,
	Icons,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
} from "@sembark-travel/ui-dialog"
import { isTruthy, SwitchInputField } from "@sembark-travel/ui-form"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect } from "react"
import { generatePath } from "../router-utils"
import {
	policyFiltersToQuery,
	policyFiltersToRequestParams,
	policyQueryToFilters,
	type TPolicyListFilters,
} from "../shared/policies"
import { paymentPoliciesXHR, TPaymentPolicyDetail } from "./store"

function paymentPolicyDetailPath(item: TPaymentPolicyDetail) {
	const params = new URLSearchParams({
		name: item.name,
		is_deleted: String(Boolean(item.deleted_at)),
		is_default: String(Boolean(item.is_default)),
		version_id: String(item.latest_policy.id),
		version: String(item.latest_policy.version),
	})
	return `${generatePath("/payment-policy/:paymentPolicyId", {
		paymentPolicyId: String(item.id),
	})}?${params.toString()}`
}

export function PaymentPoliciesList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: policyFiltersToQuery,
		fromQuery: policyQueryToFilters,
	})
	const [params, setParams] = useSearch<TPolicyListFilters>(query)
	const xhr = useXHR()

	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])

	return (
		<Search
			title="Payment Policy"
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			Filters={Filters}
			resetParams={{
				q: "",
				page: 1,
			}}
			areAdvancedFiltersApplied={(filters) => {
				const { deleted_only, ...otherFilters } = filters
				return (
					isTruthy(deleted_only) ||
					areAdvancedFiltersAppliedDefault(otherFilters)
				)
			}}
			actions={
				<ButtonLink
					to={generatePath("/payment-policy/new")}
					status="primary"
					level="primary"
				>
					Create Policy
				</ButtonLink>
			}
		>
			<ListView<TPaymentPolicyDetail, TPolicyListFilters>
				pageKey="payment-policies"
				params={params}
				fetch={(xhr, params) => {
					return paymentPoliciesXHR(xhr).get(
						policyFiltersToRequestParams(params)
					)
				}}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, refresh }) => (
					<Table
						responsive
						bordered
						headers={[
							"Policy",
							...(params.deleted_only ? ["Disabled At"] : []),
							"Last Updated",
							"",
						]}
						rows={items.map((item) => [
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Link
									to={paymentPolicyDetailPath(item)}
									color="accent"
									fontWeight="semibold"
								>
									{item.name}
								</Link>
								<Badge status={item.deleted_at ? "warning" : undefined}>
									v{item.latest_policy?.version}
								</Badge>
								{item.is_default ? <Badge warning>Default</Badge> : null}
							</Inline>,
							...(params.deleted_only
								? [
										<RelativeTime
											timestamp={item.deleted_at || undefined}
											color="warning"
										/>,
									]
								: []),
							<Stack>
								<RelativeTime timestamp={item.latest_policy?.created_at} />
								<Text color="muted" fontSize="sm">
									{item.latest_policy?.created_by?.name
										? `by ${item.latest_policy.created_by.name}`
										: ""}
								</Text>
							</Stack>,
							<Inline justifyContent="end">
								{item.is_default ? null : (
									<Dropdown alignRight>
										<Dropdown.ToggleButton size="sm" level="tertiary">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											<ConfirmationDialogProvider>
												{({ confirm }) => (
													<>
														<Dropdown.MenuItem
															color={item.deleted_at ? "success" : "warning"}
															onClick={async () => {
																if (await confirm()) {
																	try {
																		if (item.deleted_at) {
																			await paymentPoliciesXHR(xhr).restore(
																				item.id
																			)
																			showSnackbar(
																				"Policy Restored Successfully"
																			)
																		} else {
																			await paymentPoliciesXHR(xhr).disable(
																				item.id
																			)
																			showSnackbar(
																				"Policy Disabled Successfully"
																			)
																		}
																		refresh()
																	} catch (e) {
																		const error = e as Error
																		showSnackbar(error.message)
																	}
																}
															}}
														>
															{item.deleted_at ? (
																<Icons.Refresh />
															) : (
																<Icons.Ban />
															)}
															{item.deleted_at ? " Restore" : " Disable"}
														</Dropdown.MenuItem>
														<ConfirmationDialog
															title={
																item.deleted_at
																	? "Restore Policy"
																	: "Disable Policy"
															}
															destructiveAction={Boolean(!item.deleted_at)}
															acceptActionLabel={
																item.deleted_at ? "Restore" : "Disable"
															}
														>
															<Text>
																Are you sure you want to{" "}
																{item.deleted_at ? "restore" : "disable"}{" "}
																<Text as="span" fontWeight="semibold">
																	{item.name}{" "}
																</Text>
																policy ?
															</Text>
														</ConfirmationDialog>
													</>
												)}
											</ConfirmationDialogProvider>
										</Dropdown.Menu>
									</Dropdown>
								)}
							</Inline>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

export function SelectPaymentPolicyDetail({
	...props
}: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			perFetchLimit={15}
			{...props}
			fetch={(q, { page }) =>
				paymentPoliciesXHR(xhr)
					.get({ q, page, limit: 15 })
					.then((resp) => resp.data)
			}
		/>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SwitchInputField name="deleted_only" label="Disabled Only" />
		</Stack>
	)
}
